'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { SettingResponse, SettingItem } from '@/types/setting';

interface SettingsContextType {
    settings: SettingResponse | null;
    getSetting: (type: string) => SettingItem | undefined;
    getSettingContent: (type: string, defaultValue?: string) => string;
    getRawSettingContent: (type: string, defaultValue?: string) => string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
    children: ReactNode;
    settings: SettingResponse | null;
}

export function SettingsProvider({ children, settings }: SettingsProviderProps) {
    const getSetting = (type: string) => {
        return settings?.data?.find((item) => item.type === type);
    };

    const getSettingContent = (type: string, defaultValue: string = '') => {
        const item = getSetting(type);
        if (item && item.content) {
            // Strip HTML tags as requested
            return item.content.replace(/<[^>]*>?/gm, '');
        }
        return defaultValue;
    };

    const getRawSettingContent = (type: string, defaultValue: string = '') => {
        const item = getSetting(type);
        if (item && item.content) {
            return item.content;
        }
        return defaultValue;
    };

    return (
        <SettingsContext.Provider value={{ settings, getSetting, getSettingContent, getRawSettingContent }}>
            {children}
        </SettingsContext.Provider>
    );
}

export function useSettings() {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
}
